import { RootState } from 'duck/store'
import { getAvailableFinderStages, getFinderStage, Stage } from 'entities-next/stages'
import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { MenuButton } from 'scenes/Dashboard/components/Buttons'
import { getDeal } from 'scenes/Dashboard/data/deals/selectors'
import { Deal } from 'scenes/Dashboard/data/deals/types'
import { updateDealFinderStage } from 'scenes/Dashboard/scenes/DealManager/duck/actions'

export type MoveToButtonProps = {
  className?: string
  // TODO: Make this prop available to all components
  'data-testid'?: string
  dealId: Deal['id']
}

function useMoveToButtonData(dealId: Deal['id']) {
  const availableFinderStages = getAvailableFinderStages()
  const currentStage = useSelector((state: RootState) => {
    const deal = getDeal(state, dealId)
    return deal?.currentFinderStage ? getFinderStage(deal.currentFinderStage) : undefined
  })

  const options = useMemo(
    () => availableFinderStages.map((finderStage) => ({ label: finderStage.display.label, value: finderStage })),
    [availableFinderStages]
  )
  const label = currentStage ? currentStage.display.label : 'Not in finder'

  return {
    options,
    label,
  }
}

function useMoveToHandlers(dealId: Deal['id']) {
  const dispatch = useDispatch()
  const onSelect = useCallback(
    (newStage: Stage) => {
      dispatch(
        updateDealFinderStage.request({
          dealId: dealId!,
          newStage: newStage.id,
        })
      )
    },
    [dealId, dispatch]
  )
  return {
    onSelect,
  }
}

export const MoveToButton = React.memo(({ className, dealId, ...otherProps }: MoveToButtonProps) => {
  const { options, label } = useMoveToButtonData(dealId)
  const { onSelect } = useMoveToHandlers(dealId)

  return (
    <MenuButton className={className} options={options} onSelect={onSelect} {...otherProps}>
      {label}
    </MenuButton>
  )
})

MoveToButton.displayName = 'MoveToButton'
